import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@components/global/Layout';
import { getLandingPageByUid } from '@adapters/prismic/fetching/landingpage';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type {
  GetStaticPropsContext,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { PageType } from '@adapters/prismic/types/slices';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import getSeoComponentByUid from '@adapters/prismic/fetching/seoComponent/getSeoComponentByPath';
import SeoContentComponent, {
  SeoContentData,
} from '@components/global/SeoContentComponent/SeoContentComponent';
import LandingPageSliceZone from '@components/cms/LandingPageSliceZone';
import { BrandedStoreSliceType } from '@adapters/prismic/types/brandedStorePage';
import { handlePromoDetailsTracking } from '@utils/gtm';
import {
  AlgonomyPlacement,
  RecommendationsLoader,
} from '@components/product/ProductCollectionComponent';
import { LandingPageDocumentDataBody1RecommendationsSlicePrimary } from 'prismicio-types';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';

interface LandingPageProps {
  landingPage: any;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  seoContentData?: SeoContentData;
  stores: MyStoreData[] | null;
  metaTitle: string;
  metaDescription: string;
  recommendationsCategory?: string;
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<LandingPageProps>
> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Landing page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const landingPage = await getLandingPageByUid(
    params.slug,
    pageLocale,
    previewRef
  );

  if (!landingPage) {
    console.error(`Landing page with slug '${params?.slug}' not found`);
    return {
      notFound: true,
    };
  }

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    seoComponentData,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common']),
    getSeoComponentByUid(params?.slug, locale),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const metaTitle = landingPage?.meta_title;
  const metaDescription = landingPage?.meta_description;

  const seoContentData = {
    title: (seoComponentData && seoComponentData?.title) ?? null,
    title_position:
      (seoComponentData && seoComponentData?.title_position) ?? null,
    body: (seoComponentData && seoComponentData?.body?.[0]?.text) ?? null,
  };

  const canonicalUrl = localePathAbs(locale, `/p/${params.slug}`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/p/${params.slug}`) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  const recommendationsCategory =
    (
      landingPage.body1.find(
        (slice: { slice_type: string }) =>
          slice.slice_type === 'recommendations'
      )?.primary as LandingPageDocumentDataBody1RecommendationsSlicePrimary
    )?.category ?? null;

  return {
    props: {
      metaTitle,
      metaDescription,
      stores,
      landingPage,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      seoContentData,
      hideTopRibbonAndStoreSelector: true,
      recommendationsCategory,
      promoBarData,
    },
    revalidate: 1,
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function LandingPageComponent({
  metaTitle,
  metaDescription,
  landingPage,
  canonicalUrl,
  langAltUrl,
  xDefault,
  seoContentData,
  recommendationsCategory,
}: LandingPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const heroSlice = landingPage.body1.find(
    (slice: { slice_type: string }) =>
      slice.slice_type === 'hero_image' ||
      slice.slice_type === BrandedStoreSliceType.hero
  );
  let heroImage;
  if (heroSlice) {
    heroImage = heroSlice.primary.image;
  }

  const [recommendations, setRecommendations] = useState<
    AlgonomyPlacement[] | null
  >(null);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={
          heroImage
            ? {
                title: metaTitle,
                description: metaDescription,
                locale,
                images: [
                  {
                    url: heroImage?.url,
                    width: heroImage?.dimensions?.width,
                    height: heroImage?.dimensions?.height,
                    alt: heroImage?.alt,
                  },
                ],
              }
            : {
                title: metaTitle,
                description: metaDescription,
                locale,
              }
        }
      />
      <RecommendationsLoader
        pageType="landingPage"
        data={{
          category: recommendationsCategory,
        }}
        onLoad={(data) => {
          setRecommendations(data);
        }}
        trigger={recommendationsCategory !== undefined}
      />
      <LandingPageSliceZone
        slices={landingPage.body1}
        pageType={PageType.landingPage}
        recommendations={recommendations}
        recommendationsCategory={recommendationsCategory}
      />
      <SeoContentComponent data={seoContentData} />
    </>
  );
}

LandingPageComponent.Layout = Layout;
